import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Mic, MicOff, Minimize2, Send, Volume2, VolumeX, X } from 'lucide-react';
import * as api from '../api';

const WAKE_REGEX = /\b(ei|e|eu|hey|ok|okay|o\s*que)\W+foto(?:\W+ai)?\b/i;
const YES_REGEX = /\b(sim|confirmo|pode|ok|autorizo)\b/i;
const NO_REGEX = /\b(nao|não|cancela|cancelar|negativo)\b/i;
const MAX_LOG_LINES = 10;
const MIN_COMMAND_LENGTH = 8;
const MIN_COMMAND_WORDS = 2;
const DEFAULT_SESSION_MESSAGE = 'Sessão expirada, faça login novamente.';
const IS_MOBILE_UA =
  typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
const WAKE_RESTART_DELAY_MS = IS_MOBILE_UA ? 1400 : 280;

const SpeechRecognitionCtor =
  typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function plusDays(baseDate, days) {
  const d = new Date(baseDate);
  d.setDate(d.getDate() + days);
  return d;
}

function sanitizeSpeechText(value) {
  return String(value || '')
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/\bwww\.\S+/gi, ' ')
    .replace(/\b\S+@\S+\.\S+\b/gi, ' ')
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, ' ')
    .replace(/[#!*<>{}|\\^~]+/g, ' ')
    .replace(/\b(emote|emoji|asterisco|ponto de exclamação|ponto de exclamacao)\b/gi, ' ')
    .replace(/[^\p{L}\p{M}\p{N}\s.,;:!?()%\-/'"]/gu, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function parseQuickIntent(rawText) {
  const text = String(rawText || '').trim();
  const normalized = normalizeText(text);

  const isCreateRequest = /\b(lancar|lançar|criar|abre|abrir)\b/.test(normalized);
  const assigneeMatch = normalized.match(/\batribuir\s+(?:ao|a|para)\s+([a-z0-9._-]+)/);
  const categoryMatch = normalized.match(/\bcategoria\s+([a-z0-9 _-]{2,40})/);
  const locationMatch = normalized.match(/\b(?:em|na|no)\s+([a-z0-9 _-]{3,60})(?=\s+(?:com|para|pra|ate|até|hoje|amanha|amanhã|e\b)|$)/);

  const now = new Date();
  const today = toIsoDate(now);
  const tomorrow = toIsoDate(plusDays(now, 1));

  let startDate = null;
  let endDate = null;

  if (/\b(comeca|comeca|inicia|inicio)\s+hoje\b/.test(normalized) || /\bhoje\b/.test(normalized)) {
    startDate = today;
  }
  if (/\b(termina|finaliza|fim)\s+(amanha|amanhã)\b/.test(normalized) || /\bamanha\b/.test(normalized)) {
    endDate = tomorrow;
  }

  return {
    isCreateRequest,
    categoryHint: categoryMatch ? String(categoryMatch[1] || '').trim() : '',
    locationHint: locationMatch ? String(locationMatch[1] || '').trim() : '',
    assigneeName: assigneeMatch ? String(assigneeMatch[1] || '').trim() : '',
    startDate,
    endDate,
    rawCommand: text
  };
}

function isCommandTooShort(value) {
  const text = String(value || '').trim();
  const words = text.split(/\s+/).filter(Boolean);
  return text.length < MIN_COMMAND_LENGTH || words.length < MIN_COMMAND_WORDS;
}

export default function FotoAiWidget({ currentUser, activeRoute = '', activeTaskId = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [status, setStatus] = useState('inicializando');
  const [logs, setLogs] = useState([]);
  const [manualText, setManualText] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [pendingQuickIntent, setPendingQuickIntent] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [micError, setMicError] = useState('');
  const [sessionError, setSessionError] = useState('');
  const [listeningEnabled, setListeningEnabled] = useState(true);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayStatus, setOverlayStatus] = useState('Aguardando ativação...');
  const [googleConnected, setGoogleConnected] = useState(false);

  const wakeRecognitionRef = useRef(null);
  const commandRecognitionRef = useRef(null);
  const startedRef = useRef(false);
  const wakeDebounceRef = useRef(0);
  const overlayCloseTimerRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const sessionRecoveryRef = useRef(false);
  const wakeRestartTimerRef = useRef(null);
  const recognitionModeRef = useRef('idle');
  const wakeRunningRef = useRef(false);
  const commandRunningRef = useRef(false);
  const wakeBackoffMsRef = useRef(WAKE_RESTART_DELAY_MS);
  const wakeStartTsRef = useRef(0);
  const rapidWakeEndsRef = useRef(0);
  const preferredVoiceRef = useRef(null);
  const audioRef = useRef(null);

  const supportsWakeWord = Boolean(SpeechRecognitionCtor);

  const statusLabel = useMemo(() => {
    if (document.hidden) return 'pausado';
    if (sessionError) return 'sessao';
    if (micError) return 'erro';
    if (isProcessing) return 'processando';
    return status;
  }, [status, isProcessing, micError, sessionError]);

  const pushLog = (text, type = 'info') => {
    const item = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      text
    };
    setLogs((prev) => [item, ...prev].slice(0, MAX_LOG_LINES));
  };

  const clearOverlayCloseTimer = () => {
    if (overlayCloseTimerRef.current) {
      clearTimeout(overlayCloseTimerRef.current);
      overlayCloseTimerRef.current = null;
    }
  };

  const clearWakeRestartTimer = () => {
    if (wakeRestartTimerRef.current) {
      clearTimeout(wakeRestartTimerRef.current);
      wakeRestartTimerRef.current = null;
    }
  };

  const scheduleWakeRestart = () => {
    clearWakeRestartTimer();
    wakeRestartTimerRef.current = setTimeout(() => {
      if (
        recognitionModeRef.current === 'wake' &&
        !document.hidden &&
        !isProcessing &&
        !micError &&
        !sessionError &&
        currentUser
      ) {
        startWakeRecognition();
      }
    }, wakeBackoffMsRef.current);
  };

  const closeOverlay = () => {
    clearOverlayCloseTimer();
    setOverlayVisible(false);
    setOverlayStatus('Aguardando ativação...');
  };

  const scheduleOverlayClose = (delayMs = 1100) => {
    clearOverlayCloseTimer();
    overlayCloseTimerRef.current = setTimeout(() => {
      setOverlayVisible(false);
      setOverlayStatus('Aguardando ativação...');
    }, delayMs);
  };

  const stopSpeaking = () => {
    if (window.speechSynthesis?.speaking) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const pickBestVoice = () => {
    if (!window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;

    const normalize = (v) => String(v || '').toLowerCase();
    const byPriority = [
      (v) => normalize(v.lang) === 'pt-br' && /google portugu[eê]s do brasil/.test(normalize(v.name)),
      (v) => normalize(v.lang) === 'pt-br' && /francisca|maria|brenda|luciana|antonio|daniel/.test(normalize(v.name)),
      (v) => normalize(v.lang) === 'pt-br' && /neural|natural|online/.test(normalize(v.name)),
      (v) => normalize(v.lang) === 'pt-br',
      (v) => normalize(v.lang).startsWith('pt-'),
      (v) => normalize(v.lang).startsWith('pt')
    ];

    for (const rule of byPriority) {
      const found = voices.find(rule);
      if (found) {
        preferredVoiceRef.current = found;
        return found;
      }
    }

    preferredVoiceRef.current = voices[0] || null;
    return preferredVoiceRef.current;
  };

  const speak = async (text) => {
    if (isMuted) return;
    const clean = sanitizeSpeechText(text);
    if (!clean) return;

    stopSpeaking();

    try {
      const res = await api.fotoAiTts(clean);
      if (res?.ok && res?.provider === 'deepgram' && res?.audioBase64) {
        const audioData = atob(res.audioBase64);
        const arrayBuffer = new ArrayBuffer(audioData.length);
        const view = new Uint8Array(arrayBuffer);
        for (let i = 0; i < audioData.length; i++) {
          view[i] = audioData.charCodeAt(i) & 0xFF;
        }
        const blob = new Blob([arrayBuffer], { type: res.mimeType || 'audio/mp3' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { URL.revokeObjectURL(url); };
        await audio.play();
        return;
      }
    } catch (_) {}

    if (!window.speechSynthesis) return;

    const utter = new SpeechSynthesisUtterance(clean);
    const best = preferredVoiceRef.current || pickBestVoice();
    utter.lang = best?.lang || 'pt-BR';
    if (best) utter.voice = best;
    utter.rate = 0.96;
    utter.pitch = 1;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
  };

  const stopWakeRecognition = () => {
    recognitionModeRef.current = 'idle';
    clearWakeRestartTimer();
    wakeRunningRef.current = false;
    if (!wakeRecognitionRef.current) return;
    try {
      wakeRecognitionRef.current.onend = null;
      wakeRecognitionRef.current.stop();
    } catch (error) {
      // ignore
    }
  };

  const stopCommandRecognition = () => {
    commandRunningRef.current = false;
    if (!commandRecognitionRef.current) return;
    try {
      commandRecognitionRef.current.onend = null;
      commandRecognitionRef.current.stop();
    } catch (error) {
      // ignore
    }
  };

  const pauseListeningForSession = (message = DEFAULT_SESSION_MESSAGE) => {
    setSessionError(message);
    setStatus('pausado');
    setIsProcessing(false);
    stopWakeRecognition();
    stopCommandRecognition();
    closeOverlay();
  };

  const clearSessionPause = () => {
    setSessionError('');
  };

  const hasValidSession = () => api.hasSessionToken();

  const handleApiError = (error) => {
    const code = String(error?.code || '').trim();
    if (code === 'SESSION_MISSING_TOKEN' || code === 'SESSION_INVALID_TOKEN') {
      pauseListeningForSession(error?.message || DEFAULT_SESSION_MESSAGE);
      return true;
    }
    return false;
  };

  const ensureSessionReady = async ({ silent = false } = {}) => {
    if (sessionRecoveryRef.current) return false;
    sessionRecoveryRef.current = true;
    try {
      if (!hasValidSession()) {
        pauseListeningForSession(DEFAULT_SESSION_MESSAGE);
        return false;
      }
      await api.fotoAiHealth();
      const googleStatus = await api.fotoAiGoogleStatus().catch(() => ({ connected: false }));
      setGoogleConnected(Boolean(googleStatus?.connected));
      if (sessionError) {
        clearSessionPause();
        if (!silent) {
          pushLog('Sessão restaurada. Pode falar com a Foto AI.', 'success');
        }
      }
      return true;
    } catch (error) {
      handleApiError(error);
      return false;
    } finally {
      sessionRecoveryRef.current = false;
    }
  };

  const startWakeRecognition = () => {
    if (!listeningEnabled) return;
    if (!supportsWakeWord || document.hidden || isProcessing || micError || sessionError || !currentUser) {
      return;
    }
    if (wakeRunningRef.current || commandRunningRef.current) return;
    if (!hasValidSession()) {
      pauseListeningForSession(DEFAULT_SESSION_MESSAGE);
      return;
    }

    if (!wakeRecognitionRef.current) {
      const recognition = new SpeechRecognitionCtor();
      recognition.lang = 'pt-BR';
      recognition.continuous = !IS_MOBILE_UA;
      recognition.interimResults = !IS_MOBILE_UA;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          transcript += ` ${event.results[i][0].transcript || ''}`;
        }
        const normalized = normalizeText(transcript);
        if (!normalized || !WAKE_REGEX.test(normalized)) return;

        const now = Date.now();
        if (now - wakeDebounceRef.current < 2000) return;
        wakeDebounceRef.current = now;

        stopSpeaking();
        pushLog('Wake word detectada: ei foto', 'wake');
        setOverlayVisible(true);
        setOverlayStatus('Ativação detectada. Fale seu comando...');
        recognitionModeRef.current = 'command';

        try {
          recognition.stop();
        } catch (error) {
          // ignore
        }

        setTimeout(() => {
          startCommandRecognition();
        }, 120);
      };

      recognition.onerror = (event) => {
        if (event.error === 'no-speech' || event.error === 'aborted') return;
        pushLog(`Erro no wake word: ${event.error}`, 'error');
        wakeBackoffMsRef.current = Math.min(wakeBackoffMsRef.current + 800, 6000);
        if (event.error === 'not-allowed') {
          setMicError('Microfone bloqueado no navegador. Libere a permissão e recarregue a página.');
          setStatus('texto');
          return;
        }
      };

      recognition.onend = () => {
        wakeRunningRef.current = false;
        if (recognitionModeRef.current === 'wake') {
          const elapsed = Date.now() - (wakeStartTsRef.current || 0);
          if (elapsed > 0 && elapsed < 1100) {
            rapidWakeEndsRef.current += 1;
          } else {
            rapidWakeEndsRef.current = 0;
          }
          if (rapidWakeEndsRef.current >= 4) {
            recognitionModeRef.current = 'idle';
            setStatus('texto');
            setMicError('Escuta instavel neste dispositivo. Toque em MIC para reconectar.');
            pushLog('Escuta por voz pausada por instabilidade.', 'warning');
            return;
          }
        }
        if (recognitionModeRef.current === 'wake') {
          scheduleWakeRestart();
        }
      };

      wakeRecognitionRef.current = recognition;
    }

    setStatus('wake-listening');
    recognitionModeRef.current = 'wake';
    clearWakeRestartTimer();
    try {
      wakeStartTsRef.current = Date.now();
      wakeRecognitionRef.current.start();
      wakeRunningRef.current = true;
      wakeBackoffMsRef.current = WAKE_RESTART_DELAY_MS;
    } catch (error) {
      // already started
      wakeRunningRef.current = false;
      wakeBackoffMsRef.current = Math.min(wakeBackoffMsRef.current + 800, 6000);
    }
  };

  const handleGoogleConnectToggle = async () => {
    try {
      if (!googleConnected) {
        const payload = await api.fotoAiGoogleConnectUrl();
        if (payload?.url) {
          window.open(payload.url, '_blank', 'noopener,noreferrer,width=560,height=760');
          pushLog('Abra a aba do Google e conclua a autorização.', 'warning');
          setTimeout(async () => {
            const status = await api.fotoAiGoogleStatus().catch(() => ({ connected: false }));
            setGoogleConnected(Boolean(status?.connected));
            if (status?.connected) {
              pushLog('Google conectado com sucesso.', 'success');
            }
          }, 3500);
        }
        return;
      }
      await api.fotoAiGoogleDisconnect();
      setGoogleConnected(false);
      pushLog('Google desconectado desta conta.', 'warning');
    } catch (error) {
      if (handleApiError(error)) return;
      pushLog(error.message || 'Falha ao conectar Google.', 'error');
    }
  };

  const handleConfirmDecision = async (rawText) => {
    const normalized = normalizeText(rawText);
    const isYes = YES_REGEX.test(normalized);
    const isNo = NO_REGEX.test(normalized);

    if (!isYes && !isNo) {
      const hint = 'Responda apenas sim ou não.';
      pushLog(hint, 'warning');
      setOverlayStatus(hint);
      speak(hint);
      return;
    }

    const actionId = pendingConfirmation?.actionId;
    if (!actionId) return;

    setIsProcessing(true);
    setStatus('processing');
    setOverlayVisible(true);
    setOverlayStatus('Confirmando ação...');

    try {
      const response = await api.fotoAiConfirm({
        actionId,
        approve: isYes,
        conversationId
      });
      setPendingConfirmation(null);
      const message = response.message || (isYes ? 'Acao confirmada.' : 'Acao cancelada.');
      pushLog(`Foto AI: ${message}`, isYes ? 'assistant' : 'warning');
      setOverlayStatus(message);
      speak(message);
      scheduleOverlayClose(900);
    } catch (error) {
      if (handleApiError(error)) return;
      pushLog(error.message || 'Falha ao confirmar ação.', 'error');
      setOverlayStatus('Falha ao confirmar.');
      speak('Não consegui confirmar essa ação agora.');
      scheduleOverlayClose(1000);
    } finally {
      setIsProcessing(false);
      setStatus('wake-listening');
      startWakeRecognition();
    }
  };

  const sendChat = async (text, quickIntent = null, source = 'texto') => {
    const ready = await ensureSessionReady({ silent: true });
    if (!ready) return;

    setIsProcessing(true);
    setStatus('processing');
    setOverlayVisible(true);
    setOverlayStatus('Processando comando...');

    try {
      const response = await api.fotoAiChat({
        message: text,
        conversationId,
        quickIntent,
        uiContext: {
          activeRoute: activeRoute || '',
          activeTaskId: activeTaskId || null
        }
      });

      setConversationId(response.conversationId || null);
      setPendingConfirmation(response.confirmation || null);

      const reply = String(response.response || 'Sem resposta.').trim();
      pushLog(`Foto AI: ${reply}`, 'assistant');
      setOverlayStatus(reply);
      speak(reply);

      if (!response.confirmation) {
        scheduleOverlayClose(900);
      }
    } catch (error) {
      if (handleApiError(error)) return;
      const message = error.message || 'Falha ao consultar Foto AI.';
      pushLog(message, 'error');
      setOverlayStatus(message);
      speak('Não consegui processar esse pedido agora.');
      scheduleOverlayClose(1000);
    } finally {
      setIsProcessing(false);
      setStatus('wake-listening');
      startWakeRecognition();
    }
  };

  const handleCommand = async (rawText, source = 'voz') => {
    const text = String(rawText || '').trim();
    if (!text) return;

    pushLog(`Você (${source}): ${text}`, 'user');

    if (pendingConfirmation) {
      await handleConfirmDecision(text);
      return;
    }

    if (pendingQuickIntent?.awaitingCategory) {
      const quickIntent = {
        ...pendingQuickIntent,
        categoryHint: text,
        locationHint: text,
        awaitingCategory: false
      };
      setPendingQuickIntent(null);
      await sendChat(quickIntent.rawCommand || text, quickIntent, source);
      return;
    }

    const quickIntent = parseQuickIntent(text);
    if (quickIntent.isCreateRequest && !quickIntent.categoryHint && !quickIntent.locationHint) {
      const question = 'Qual categoria ou grupo devo usar para lancar essa tarefa?';
      setPendingQuickIntent({ ...quickIntent, awaitingCategory: true });
      pushLog(`Foto AI: ${question}`, 'assistant');
      setOverlayVisible(true);
      setOverlayStatus(question);
      speak(question);
      return;
    }

    const shouldSendQuickIntent = quickIntent.isCreateRequest;
    await sendChat(text, shouldSendQuickIntent ? quickIntent : null, source);
  };

  const startCommandRecognition = () => {
    if (!supportsWakeWord || document.hidden || sessionError) return;

    stopCommandRecognition();
    recognitionModeRef.current = 'command';
    commandRunningRef.current = false;
    setStatus('command-listening');
    setOverlayStatus('Ouvindo comando...');

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = 'pt-BR';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    let finalText = '';
    let interimText = '';

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const chunk = String(event.results[i][0].transcript || '').trim();
        if (!chunk) continue;
        if (event.results[i].isFinal) {
          finalText = `${finalText} ${chunk}`.trim();
          interimText = '';
        } else {
          interimText = chunk;
        }
      }
      const preview = `${finalText} ${interimText}`.trim();
      if (preview) {
        setOverlayStatus(`Comando: ${preview}`);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      pushLog(`Erro na captura: ${event.error}`, 'error');
    };

    recognition.onend = async () => {
      commandRunningRef.current = false;
      const transcript = `${finalText} ${interimText}`.trim();
      if (!transcript || isCommandTooShort(transcript)) {
        const msg = 'Não entendi o comando completo. Fale novamente após ei foto.';
        pushLog(msg, 'warning');
        setOverlayStatus(msg);
        speak(msg);
        scheduleOverlayClose(900);
        setStatus('wake-listening');
        recognitionModeRef.current = 'wake';
        startWakeRecognition();
        return;
      }
      await handleCommand(transcript, 'voz');
    };

    commandRecognitionRef.current = recognition;
    try {
      recognition.start();
      commandRunningRef.current = true;
    } catch (error) {
      commandRunningRef.current = false;
      setStatus('erro');
      pushLog('Falha ao iniciar captura de comando.', 'error');
      closeOverlay();
    }
  };

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      if (!currentUser) {
        startedRef.current = false;
        return;
      }
      if (startedRef.current) return;
      startedRef.current = true;
      setStatus('inicializando');

      if (!hasValidSession()) {
        pauseListeningForSession(DEFAULT_SESSION_MESSAGE);
        return;
      }

      try {
        await api.fotoAiHealth();
        const googleStatus = await api.fotoAiGoogleStatus().catch(() => ({ connected: false }));
        if (!mounted) return;
        setGoogleConnected(Boolean(googleStatus?.connected));
        clearSessionPause();
      } catch (error) {
        if (handleApiError(error)) return;
        if (mounted) {
          setMicError(error.message || 'Foto AI indisponivel.');
          pushLog('Foto AI indisponivel no momento.', 'error');
        }
        return;
      }

      if (!supportsWakeWord) {
        setMicError('Wake word indisponivel neste navegador. Use modo texto.');
        setStatus('texto');
        pushLog('Wake word indisponivel. Modo texto ativo.', 'warning');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());

        if (!mounted) return;
        setMicError('');
        if (listeningEnabled) {
          setStatus('wake-listening');
          pushLog("Foto AI online. Diga: ei foto ou ok foto", 'success');
          startWakeRecognition();
        } else {
          setStatus('pausado');
        }
      } catch (error) {
        if (!mounted) return;
        setMicError('Permissão de microfone negada. Use modo texto.');
        setStatus('texto');
        pushLog('Permissão de microfone negada. Modo texto ativo.', 'warning');
      }
    }

    bootstrap();

    const onVisibility = () => {
      if (document.hidden) {
        stopWakeRecognition();
        stopCommandRecognition();
        closeOverlay();
        setStatus('pausado');
      } else if (listeningEnabled && !micError && !sessionError && !isProcessing && currentUser) {
        setStatus('wake-listening');
        recognitionModeRef.current = 'wake';
        startWakeRecognition();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mounted = false;
      clearOverlayCloseTimer();
      clearWakeRestartTimer();
      stopWakeRecognition();
      stopCommandRecognition();
      stopSpeaking();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, listeningEnabled]);

  useEffect(() => {
    if (!sessionError || !currentUser) return undefined;
    const timer = setInterval(async () => {
      const restored = await ensureSessionReady({ silent: true });
      if (restored && listeningEnabled) {
        clearSessionPause();
        setStatus('wake-listening');
        startWakeRecognition();
      }
    }, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionError, currentUser, listeningEnabled]);

  useEffect(() => {
    if (!overlayVisible) return undefined;
    const canvas = overlayCanvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let animationFrame = null;
    const particles = [];
    const linksDistance = 130;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const total = Math.max(120, Math.floor((canvas.width * canvas.height) / 13000));
      particles.length = 0;
      for (let i = 0; i < total; i += 1) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.45,
          vy: (Math.random() - 0.5) * 0.45,
          r: Math.random() * 1.8 + 0.6
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(3, 8, 17, 0.82)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x <= 0 || p.x >= canvas.width) p.vx *= -1;
        if (p.y <= 0 || p.y >= canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 243, 255, 0.85)';
        ctx.fill();

        for (let j = i + 1; j < particles.length; j += 1) {
          const n = particles[j];
          const dx = p.x - n.x;
          const dy = p.y - n.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > linksDistance) continue;
          const alpha = 1 - dist / linksDistance;
          ctx.strokeStyle = `rgba(0, 243, 255, ${alpha * 0.22})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(n.x, n.y);
          ctx.stroke();
        }
      }

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const glow = ctx.createRadialGradient(centerX, centerY, 35, centerX, centerY, 240);
      glow.addColorStop(0, 'rgba(0, 243, 255, 0.22)');
      glow.addColorStop(1, 'rgba(0, 243, 255, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 240, 0, Math.PI * 2);
      ctx.fill();

      animationFrame = window.requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
    };
  }, [overlayVisible]);

  useEffect(() => {
    if (!window.speechSynthesis) return undefined;
    const refreshVoices = () => {
      pickBestVoice();
    };
    refreshVoices();
    window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', refreshVoices);
    };
  }, []);

  const handleToggleListening = async () => {
    if (listeningEnabled) {
      setListeningEnabled(false);
      stopWakeRecognition();
      stopCommandRecognition();
      setStatus('pausado');
      pushLog('Escuta por voz pausada.', 'warning');
      return;
    }
    setListeningEnabled(true);
    setMicError('');
    rapidWakeEndsRef.current = 0;
    wakeBackoffMsRef.current = WAKE_RESTART_DELAY_MS;
    const ready = await ensureSessionReady({ silent: true });
    if (!ready) return;
    setStatus('wake-listening');
    startWakeRecognition();
    pushLog('Escuta por voz retomada.', 'success');
  };

  const handleManualSend = async (event) => {
    event.preventDefault();
    if (sessionError || !hasValidSession()) {
      const recovered = await ensureSessionReady();
      if (!recovered) return;
    }

    const text = manualText.trim();
    if (!text) return;
    setManualText('');
    await handleCommand(text, 'texto');
  };

  if (!currentUser) return null;

  return (
    <>
      {overlayVisible && (
        <div className="fixed inset-0 z-[140] bg-black/40 backdrop-blur-md">
          <canvas ref={overlayCanvasRef} className="absolute inset-0 h-full w-full" />
          <button
            className="absolute right-6 top-6 z-10 rounded-full border border-cyan-300/60 bg-black/60 p-2 text-cyan-200 hover:bg-black/80"
            onClick={closeOverlay}
            title="Fechar"
          >
            <X size={18} />
          </button>
          <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
            <div className="max-w-xl rounded-xl border border-cyan-400/60 bg-[#030811]/70 px-6 py-5 text-center shadow-2xl">
              <div className="mb-2 text-xs uppercase tracking-[0.3em] text-cyan-300">Foto AI Ativa</div>
              <div className="text-lg font-semibold text-cyan-100">{overlayStatus}</div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-5 right-5 z-[120]">
        {!isOpen ? (
          <button
            onClick={() => setIsOpen(true)}
            className="h-14 w-14 rounded-full bg-cyan-500/90 text-slate-950 shadow-xl border border-cyan-300 flex items-center justify-center hover:scale-105 transition"
            title="Abrir Foto AI"
          >
            <Bot size={26} />
          </button>
        ) : (
          <div className="w-[360px] max-w-[90vw] bg-[#0f172a] border border-cyan-500/50 rounded-xl shadow-2xl overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 text-cyan-300 font-semibold">
                <Bot size={16} />
                <span>Foto AI</span>
                <span className="text-[10px] uppercase text-slate-400">[{statusLabel}]</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  className={`px-2 py-1 rounded text-[10px] border ${listeningEnabled ? 'border-cyan-400 text-cyan-300' : 'border-slate-600 text-slate-400'} hover:bg-slate-800 inline-flex items-center gap-1`}
                  onClick={handleToggleListening}
                  title={listeningEnabled ? 'Pausar microfone' : 'Retomar microfone'}
                >
                  {listeningEnabled ? <Mic size={12} /> : <MicOff size={12} />}
                  {listeningEnabled ? 'MIC ON' : 'MIC OFF'}
                </button>
                <button
                  className={`px-2 py-1 rounded text-[10px] border ${googleConnected ? 'border-emerald-400 text-emerald-300' : 'border-slate-600 text-slate-300'} hover:bg-slate-800`}
                  onClick={handleGoogleConnectToggle}
                  title={googleConnected ? 'Desconectar Google' : 'Conectar Google'}
                >
                  {googleConnected ? 'GOOGLE ON' : 'GOOGLE OFF'}
                </button>
                <button
                  className={`p-1 rounded ${isMuted ? 'text-slate-500' : 'text-cyan-300'} hover:bg-slate-800`}
                  onClick={() => setIsMuted((prev) => !prev)}
                  title={isMuted ? 'Ativar voz' : 'Silenciar voz'}
                >
                  {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
                <button
                  className="p-1 rounded text-slate-400 hover:bg-slate-800"
                  onClick={() => setIsOpen(false)}
                  title="Minimizar"
                >
                  <Minimize2 size={14} />
                </button>
              </div>
            </div>

            <div className="px-3 py-2 text-xs text-slate-400 border-b border-slate-800">
              {sessionError || micError || "Em segundo plano da aba: diga 'ei foto' ou 'ok foto' para ativar."}
            </div>

            <div className="max-h-56 overflow-auto p-3 space-y-2 bg-[#020617]">
              {logs.length === 0 ? (
                <div className="text-xs text-slate-500">Aguardando comandos...</div>
              ) : (
                logs.map((entry) => (
                  <div
                    key={entry.id}
                    className={`text-xs rounded px-2 py-1 ${
                      entry.type === 'error'
                        ? 'bg-red-900/30 text-red-300'
                        : entry.type === 'warning'
                          ? 'bg-yellow-900/20 text-yellow-300'
                          : entry.type === 'assistant'
                            ? 'bg-cyan-900/20 text-cyan-200'
                            : 'bg-slate-800 text-slate-300'
                    }`}
                  >
                    {entry.text}
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleManualSend} className="p-2 border-t border-slate-800 flex items-center gap-2">
              <input
                value={manualText}
                onChange={(event) => setManualText(event.target.value)}
                className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200 outline-none focus:border-cyan-500"
                placeholder={pendingConfirmation ? 'Responda: sim ou não' : 'Digite um comando para o Foto AI'}
                disabled={isProcessing}
              />
              <button
                type="submit"
                className="h-9 w-9 rounded bg-cyan-500 text-slate-950 flex items-center justify-center disabled:opacity-50"
                disabled={isProcessing || !manualText.trim()}
                title="Enviar"
              >
                <Send size={14} />
              </button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}


